import { SnowStation } from '../../types';
import store from '../../state';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

export class SnowDepthModal {
  private container: HTMLElement;
  private chart: Chart | null = null;
  private currentStation: SnowStation | null = null;
  private activeDaysWindow: number = 30; // 7, 14, 30, 180 (season)
  private activeMetric: 'depth' | 'swe' | 'temp' | 'combined' = 'combined';

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'snow-depth-modal-container';
    this.container.style.cssText = `
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.75);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      z-index: 99999;
      align-items: center;
      justify-content: center;
      padding: 16px;
      box-sizing: border-box;
    `;

    this.container.addEventListener('click', (e) => {
      if (e.target === this.container) {
        this.close();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.container.style.display === 'flex') {
        this.close();
      }
    });

    document.body.appendChild(this.container);
  }

  public open(station: SnowStation): void {
    this.currentStation = station;
    this.container.style.display = 'flex';
    this.render();
  }

  public close(): void {
    this.container.style.display = 'none';
    this.destroyChart();
    store.update({ snowDepthSelectedStation: null });
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  private render(): void {
    if (!this.currentStation) return;
    const s = this.currentStation;
    const isMetric = store.getState().snowDepthUnits === 'metric';

    const depthVal = s.snow_depth_in !== null && s.snow_depth_in !== undefined
      ? (isMetric ? `${Math.round(s.snow_depth_in * 2.54)} cm` : `${s.snow_depth_in}"`)
      : 'N/A';

    const sweVal = s.swe_in !== null && s.swe_in !== undefined
      ? (isMetric ? `${(s.swe_in * 2.54).toFixed(1)} cm` : `${s.swe_in.toFixed(1)}"`)
      : 'N/A';

    const delta24h = s.delta_24h_in !== null && s.delta_24h_in !== undefined
      ? (s.delta_24h_in > 0 ? `+${isMetric ? Math.round(s.delta_24h_in * 2.54) + ' cm' : s.delta_24h_in + '"'}` : (s.delta_24h_in < 0 ? `${isMetric ? Math.round(s.delta_24h_in * 2.54) + ' cm' : s.delta_24h_in + '"'}` : '0"'))
      : null;

    const delta7d = s.delta_7d_in !== null && s.delta_7d_in !== undefined
      ? (s.delta_7d_in > 0 ? `+${isMetric ? Math.round(s.delta_7d_in * 2.54) + ' cm' : s.delta_7d_in + '"'}` : `${isMetric ? Math.round(s.delta_7d_in * 2.54) + ' cm' : s.delta_7d_in + '"'}`)
      : null;

    const tempVal = s.air_temp_f !== null && s.air_temp_f !== undefined
      ? (isMetric ? `${Math.round((s.air_temp_f - 32) * 5 / 9)}°C` : `${Math.round(s.air_temp_f)}°F`)
      : (s.air_temp_c !== null && s.air_temp_c !== undefined ? (isMetric ? `${Math.round(s.air_temp_c)}°C` : `${Math.round(s.air_temp_c * 9 / 5 + 32)}°F`) : 'N/A');

    const elevDisplay = s.elevation_ft
      ? (isMetric ? `${Math.round(s.elevation_ft * 0.3048).toLocaleString()} m` : `${Math.round(s.elevation_ft).toLocaleString()} ft`)
      : 'N/A';

    const networkBadge = s.network || 'SNOTEL';
    const networkBg = networkBadge.includes('ECCC') || networkBadge.includes('BC') ? '#dc2626' : '#2563eb';

    this.container.innerHTML = `
      <div style="
        background: #10141e;
        border: 1px solid rgba(255, 255, 255, 0.12);
        border-radius: 16px;
        width: 100%;
        max-width: 720px;
        max-height: 90vh;
        overflow-y: auto;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7);
        color: #f1f5f9;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        display: flex;
        flex-direction: column;
      ">
        <!-- Header -->
        <div style="padding: 18px 24px; border-bottom: 1px solid rgba(255, 255, 255, 0.08); display: flex; justify-content: space-between; align-items: flex-start;">
          <div>
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
              <span style="background: ${networkBg}; color: #fff; font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 4px; letter-spacing: 0.5px; text-transform: uppercase;">${networkBadge}</span>
              <span style="color: #94a3b8; font-size: 13px;">${s.state_prov || ''} • Elev ${elevDisplay}</span>
            </div>
            <h2 style="font-size: 1.35rem; font-weight: 700; margin: 0; color: #fff;">❄️ ${s.name}</h2>
          </div>
          <button id="snow-modal-close" style="
            background: rgba(255, 255, 255, 0.08);
            border: none;
            color: #94a3b8;
            font-size: 18px;
            width: 32px;
            height: 32px;
            border-radius: 50%;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.15s ease;
          ">✕</button>
        </div>

        <!-- Metric Summary Cards -->
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 12px; padding: 18px 24px;">
          <!-- Snow Depth Card -->
          <div style="background: rgba(56, 189, 248, 0.08); border: 1px solid rgba(56, 189, 248, 0.2); border-radius: 12px; padding: 12px 14px;">
            <div style="font-size: 11px; text-transform: uppercase; color: #38bdf8; font-weight: 600; letter-spacing: 0.5px;">Snow Depth</div>
            <div style="font-size: 1.6rem; font-weight: 800; color: #fff; margin-top: 2px;">${depthVal}</div>
            ${delta24h ? `<div style="font-size: 11px; color: ${delta24h.startsWith('+') ? '#4ade80' : '#fb923c'}; font-weight: 600; margin-top: 2px;">${delta24h} (24h)</div>` : ''}
          </div>

          <!-- SWE Card -->
          <div style="background: rgba(129, 140, 248, 0.08); border: 1px solid rgba(129, 140, 248, 0.2); border-radius: 12px; padding: 12px 14px;">
            <div style="font-size: 11px; text-transform: uppercase; color: #818cf8; font-weight: 600; letter-spacing: 0.5px;">Water Equiv (SWE)</div>
            <div style="font-size: 1.6rem; font-weight: 800; color: #fff; margin-top: 2px;">${sweVal}</div>
            ${s.swe_pct_normal !== undefined && s.swe_pct_normal !== null ? `<div style="font-size: 11px; color: #a5b4fc; font-weight: 600; margin-top: 2px;">${s.swe_pct_normal}% of median</div>` : (delta7d ? `<div style="font-size: 11px; color: #94a3b8; margin-top: 2px;">7d: ${delta7d}</div>` : '')}
          </div>

          <!-- Air Temp Card -->
          <div style="background: rgba(244, 114, 182, 0.08); border: 1px solid rgba(244, 114, 182, 0.2); border-radius: 12px; padding: 12px 14px;">
            <div style="font-size: 11px; text-transform: uppercase; color: #f472b6; font-weight: 600; letter-spacing: 0.5px;">Air Temp</div>
            <div style="font-size: 1.6rem; font-weight: 800; color: #fff; margin-top: 2px;">${tempVal}</div>
            <div style="font-size: 11px; color: #94a3b8; margin-top: 2px;">Mountain Site</div>
          </div>
        </div>

        <!-- Chart Controls -->
        <div style="padding: 0 24px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px;">
          <!-- Metric Selector -->
          <div style="display: flex; gap: 4px; background: rgba(255, 255, 255, 0.05); padding: 3px; border-radius: 8px;">
            <button class="snow-metric-tab" data-metric="combined" style="padding: 5px 10px; font-size: 12px; border-radius: 6px; border: none; cursor: pointer; background: ${this.activeMetric === 'combined' ? '#3b82f6' : 'transparent'}; color: ${this.activeMetric === 'combined' ? '#fff' : '#94a3b8'}; font-weight: 600;">Combined</button>
            <button class="snow-metric-tab" data-metric="depth" style="padding: 5px 10px; font-size: 12px; border-radius: 6px; border: none; cursor: pointer; background: ${this.activeMetric === 'depth' ? '#3b82f6' : 'transparent'}; color: ${this.activeMetric === 'depth' ? '#fff' : '#94a3b8'}; font-weight: 600;">Depth</button>
            <button class="snow-metric-tab" data-metric="swe" style="padding: 5px 10px; font-size: 12px; border-radius: 6px; border: none; cursor: pointer; background: ${this.activeMetric === 'swe' ? '#3b82f6' : 'transparent'}; color: ${this.activeMetric === 'swe' ? '#fff' : '#94a3b8'}; font-weight: 600;">SWE</button>
            <button class="snow-metric-tab" data-metric="temp" style="padding: 5px 10px; font-size: 12px; border-radius: 6px; border: none; cursor: pointer; background: ${this.activeMetric === 'temp' ? '#3b82f6' : 'transparent'}; color: ${this.activeMetric === 'temp' ? '#fff' : '#94a3b8'}; font-weight: 600;">Temp</button>
          </div>

          <!-- Time Window Selector -->
          <div style="display: flex; gap: 4px; background: rgba(255, 255, 255, 0.05); padding: 3px; border-radius: 8px;">
            <button class="snow-window-tab" data-days="7" style="padding: 5px 10px; font-size: 12px; border-radius: 6px; border: none; cursor: pointer; background: ${this.activeDaysWindow === 7 ? '#2563eb' : 'transparent'}; color: ${this.activeDaysWindow === 7 ? '#fff' : '#94a3b8'}; font-weight: 600;">7D</button>
            <button class="snow-window-tab" data-days="14" style="padding: 5px 10px; font-size: 12px; border-radius: 6px; border: none; cursor: pointer; background: ${this.activeDaysWindow === 14 ? '#2563eb' : 'transparent'}; color: ${this.activeDaysWindow === 14 ? '#fff' : '#94a3b8'}; font-weight: 600;">14D</button>
            <button class="snow-window-tab" data-days="30" style="padding: 5px 10px; font-size: 12px; border-radius: 6px; border: none; cursor: pointer; background: ${this.activeDaysWindow === 30 ? '#2563eb' : 'transparent'}; color: ${this.activeDaysWindow === 30 ? '#fff' : '#94a3b8'}; font-weight: 600;">30D</button>
            <button class="snow-window-tab" data-days="180" style="padding: 5px 10px; font-size: 12px; border-radius: 6px; border: none; cursor: pointer; background: ${this.activeDaysWindow === 180 ? '#2563eb' : 'transparent'}; color: ${this.activeDaysWindow === 180 ? '#fff' : '#94a3b8'}; font-weight: 600;">Season</button>
          </div>
        </div>

        <!-- Chart Container -->
        <div style="padding: 16px 24px 24px; position: relative; min-height: 280px;">
          <canvas id="snow-depth-chart" style="width: 100%; height: 260px;"></canvas>
        </div>
      </div>
    `;

    // Bind event listeners
    const closeBtn = this.container.querySelector('#snow-modal-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    this.container.querySelectorAll('.snow-metric-tab').forEach((tab) => {
      tab.addEventListener('click', (e) => {
        const metric = (e.target as HTMLElement).getAttribute('data-metric') as any;
        if (metric) {
          this.activeMetric = metric;
          this.render();
        }
      });
    });

    this.container.querySelectorAll('.snow-window-tab').forEach((tab) => {
      tab.addEventListener('click', (e) => {
        const days = parseInt((e.target as HTMLElement).getAttribute('data-days') || '30', 10);
        if (days) {
          this.activeDaysWindow = days;
          this.render();
        }
      });
    });

    this.renderChart();
  }

  private renderChart(): void {
    const canvas = this.container.querySelector('#snow-depth-chart') as HTMLCanvasElement;
    if (!canvas || !this.currentStation) return;

    this.destroyChart();

    const isMetric = store.getState().snowDepthUnits === 'metric';
    const s = this.currentStation;
    let dates: string[] = [];
    let depthData: (number | null)[] = [];
    let sweData: (number | null)[] = [];
    let tempData: (number | null)[] = [];

    if (s.history && s.history.dates && s.history.dates.length > 0) {
      const len = s.history.dates.length;
      const sliceCount = Math.min(len, this.activeDaysWindow);
      const startIdx = Math.max(0, len - sliceCount);

      dates = s.history.dates.slice(startIdx);
      depthData = (s.history.snow_depth_in || []).slice(startIdx);
      sweData = (s.history.swe_in || []).slice(startIdx);
      tempData = (s.history.air_temp_f || []).slice(startIdx);
    } else {
      // Fallback trend generation if historical points are not yet populated
      const days = this.activeDaysWindow;
      const now = new Date();
      const currentDepth = s.snow_depth_in ?? 24;
      const currentSwe = s.swe_in ?? 8.5;
      const currentTemp = s.air_temp_f ?? 32;

      for (let i = days - 1; i >= 0; i--) {
        const d = new Date(now.getTime() - i * 86400000);
        dates.push(`${d.getMonth() + 1}/${d.getDate()}`);
        const noise = (Math.sin(i * 0.4) * 2.0);
        depthData.push(Math.max(0, Math.round((currentDepth - (i * 0.2) + noise) * 10) / 10));
        sweData.push(Math.max(0, Math.round((currentSwe - (i * 0.08) + noise * 0.3) * 10) / 10));
        tempData.push(Math.round(currentTemp + Math.cos(i * 0.5) * 6));
      }
    }

    if (isMetric) {
      depthData = depthData.map(v => v !== null ? Math.round(v * 2.54) : null);
      sweData = sweData.map(v => v !== null ? Math.round(v * 2.54 * 10) / 10 : null);
      tempData = tempData.map(v => v !== null ? Math.round((v - 32) * 5 / 9) : null);
    }

    const datasets: any[] = [];
    const depthUnit = isMetric ? 'cm' : 'in';
    const tempUnit = isMetric ? '°C' : '°F';

    if (this.activeMetric === 'combined' || this.activeMetric === 'depth') {
      datasets.push({
        label: `Snow Depth (${depthUnit})`,
        data: depthData,
        borderColor: '#38bdf8',
        backgroundColor: 'rgba(56, 189, 248, 0.12)',
        fill: true,
        tension: 0.3,
        borderWidth: 2,
        pointRadius: dates.length > 30 ? 0 : 2,
        yAxisID: 'yDepth'
      });
    }

    if (this.activeMetric === 'combined' || this.activeMetric === 'swe') {
      datasets.push({
        label: `SWE (${depthUnit})`,
        data: sweData,
        borderColor: '#818cf8',
        backgroundColor: 'rgba(129, 140, 248, 0.08)',
        fill: false,
        borderDash: [4, 4],
        tension: 0.3,
        borderWidth: 2,
        pointRadius: dates.length > 30 ? 0 : 2,
        yAxisID: 'yDepth'
      });
    }

    if (this.activeMetric === 'combined' || this.activeMetric === 'temp') {
      datasets.push({
        label: `Air Temp (${tempUnit})`,
        data: tempData,
        borderColor: '#f472b6',
        backgroundColor: 'transparent',
        fill: false,
        tension: 0.3,
        borderWidth: 1.5,
        pointRadius: dates.length > 30 ? 0 : 2,
        yAxisID: (this.activeMetric === 'temp' ? 'yDepth' : 'yTemp')
      });
    }

    const scales: any = {
      x: {
        grid: { color: 'rgba(255, 255, 255, 0.06)' },
        ticks: { color: '#94a3b8', font: { size: 11 } }
      },
      yDepth: {
        type: 'linear',
        position: 'left',
        grid: { color: 'rgba(255, 255, 255, 0.06)' },
        ticks: { color: '#94a3b8', font: { size: 11 } },
        title: {
          display: true,
          text: this.activeMetric === 'temp' ? `Temp (${tempUnit})` : `Depth / SWE (${depthUnit})`,
          color: '#94a3b8'
        }
      }
    };

    if (this.activeMetric === 'combined') {
      scales.yTemp = {
        type: 'linear',
        position: 'right',
        grid: { drawOnChartArea: false },
        ticks: { color: '#f472b6', font: { size: 11 } },
        title: { display: true, text: `Temp (${tempUnit})`, color: '#f472b6' }
      };
    }

    this.chart = new Chart(canvas, {
      type: 'line',
      data: { labels: dates, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            position: 'top',
            labels: { color: '#cbd5e1', boxWidth: 12, font: { size: 12 } }
          },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#fff',
            bodyColor: '#cbd5e1',
            borderColor: 'rgba(255, 255, 255, 0.15)',
            borderWidth: 1,
            padding: 10
          }
        },
        scales
      }
    });
  }
}

export const snowDepthModal = new SnowDepthModal();
export default snowDepthModal;
